import { cva, type VariantProps } from 'class-variance-authority';
import { Eye, EyeClosed } from 'lucide-react';
import { CarScoreIcon } from '@/components/atoms/car-score-icon';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/atoms/tooltip';
import { cn } from '@/libs/utils/cn';
import { formatDuration } from '@/libs/utils/format';
import type { CapturedAadtGrade } from '@/types/api';
import { CAPTURED_AADT_GRADE_LABELS, CAPTURED_AADT_MAX_GRADE } from '../../constants';

const routeToggleButtonVariants = cva(
  'group/segment flex-1 flex items-center gap-1 px-1.5 py-1 rounded-md border transition-all',
  {
    variants: {
      visible: {
        true: 'border-transparent hover:bg-muted/50',
        false: 'opacity-50 bg-muted/30 border-dashed border-muted-foreground/30',
      },
    },
    defaultVariants: {
      visible: true,
    },
  },
);

const eyeIconVariants = cva('flex-shrink-0 transition-opacity duration-200', {
  variants: {
    visible: {
      true: 'opacity-0 group-hover/segment:opacity-100',
      false: 'opacity-100',
    },
  },
  defaultVariants: {
    visible: true,
  },
});

interface RouteToggleButtonProps extends VariantProps<typeof routeToggleButtonVariants> {
  routeIndex: number;
  grade: CapturedAadtGrade;
  drivingTime: number;
  color: string;
  onToggle: () => void;
  className?: string;
}

export function RouteToggleButton({
  routeIndex,
  grade,
  drivingTime,
  color,
  visible,
  onToggle,
  className,
}: RouteToggleButtonProps) {
  const routeNumber = routeIndex + 1;
  const isVisible = visible ?? true;

  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(routeToggleButtonVariants({ visible }), className)}
      aria-pressed={isVisible}
      // The label stays static because aria-pressed already carries the state;
      // swapping both would announce visibility twice.
      aria-label={`Toggle route ${routeNumber}`}
    >
      {/* Color bar */}
      <div className="w-1 h-6 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />

      {/* Route label and info */}
      <div className="flex-1 flex flex-col items-start gap-0">
        <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color }}>
          Route {routeNumber}
        </span>
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help [&>div]:size-6">
                <CarScoreIcon score={grade} size="sm" />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              Driving grade: {grade}/{CAPTURED_AADT_MAX_GRADE}
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-xs font-medium text-primary-700 tabular-nums cursor-help whitespace-nowrap">
                {CAPTURED_AADT_GRADE_LABELS[grade]}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              Driving time: {formatDuration(drivingTime, { detailed: true })}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Eye icon */}
      <span className={eyeIconVariants({ visible })}>
        {isVisible ? (
          <Eye className="size-3.5 text-muted-foreground" />
        ) : (
          <EyeClosed className="size-3.5 text-muted-foreground" />
        )}
      </span>
    </button>
  );
}

export { routeToggleButtonVariants };
